import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { BACKUP_PASSPHRASE_REMINDER, backupPassphraseReminderDue } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';

/** The backup passphrase as Teslyn Cloud shows it: when and by whom it was set, never the passphrase. */
export interface Passphrase {
  readonly setAt: string | null;
  readonly setBy: string | null;
}

/** null while loading; 'unavailable' when Teslyn Cloud cannot tell (no backup passphrase in this database yet). */
export type PassphraseState = Passphrase | null | 'unavailable';

/** The organisation's backup passphrase record (tenant_backup; tenant admins and platform admins read it). */
export function useBackupPassphrase(
  sb: SupabaseClient,
  tenantId: string,
): { readonly state: PassphraseState; readonly reload: () => void } {
  const [state, setState] = useState<PassphraseState>(null);
  const reload = useCallback(() => {
    void sb
      .from('tenant_backup')
      .select('passphrase_set_at, passphrase_set_by_name')
      .eq('tenant_id', tenantId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) setState('unavailable');
        else {
          const r = data as {
            passphrase_set_at: string | null;
            passphrase_set_by_name: string | null;
          } | null;
          setState({ setAt: r?.passphrase_set_at ?? null, setBy: r?.passphrase_set_by_name ?? null });
        }
      });
  }, [sb, tenantId]);
  useEffect(reload, [reload]);
  return { state, reload };
}

/** Is the reminder due (decision 880)? The rule is the domain's. */
export function reminderDue(roles: readonly string[], state: PassphraseState): boolean {
  const known = state !== null && state !== 'unavailable';
  return backupPassphraseReminderDue(roles, known ? state.setAt : null, known);
}

/** The reminder itself: what is missing, why it matters, and "Set passphrase". */
export function PassphraseReminder({ onSet }: { readonly onSet: () => void }): ReactElement {
  return (
    <section
      className="notice notice--warn stack"
      role="status"
      aria-label={BACKUP_PASSPHRASE_REMINDER.title}
    >
      <h2 className="heading">{BACKUP_PASSPHRASE_REMINDER.title}</h2>
      <p className="body">{BACKUP_PASSPHRASE_REMINDER.text}</p>
      <div className="row">
        <Button variant="primary" onClick={onSet}>
          {BACKUP_PASSPHRASE_REMINDER.action}
        </Button>
      </div>
    </section>
  );
}

/** Home for tenant admins (decision 880): the reminder while no passphrase is set; nothing otherwise. */
export function HomePassphraseReminder({
  sb,
  tenantId,
  roles,
  onSet,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly roles: readonly string[];
  readonly onSet: () => void;
}): ReactElement | null {
  const { state } = useBackupPassphrase(sb, tenantId);
  return reminderDue(roles, state) ? <PassphraseReminder onSet={onSet} /> : null;
}
